import { useState, useMemo, useEffect } from 'react';
import {
  apiErrorText,
  useListPaymentRequestsQuery,
  useCollectPaymentRequestsMutation,
  useSubmitRequestsToCeoMutation,
  useExportApprovedTo1CMutation,
  useListPaymentRegistriesQuery,
  useDecidePaymentRequestMutation,
  useSync1CApiMutation,
  useGetFinancierDashboardQuery,
  type FinPaymentRequestRow,
  type Export1cResult,
  type Export1cInput,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import {
  IconBarChart,
  IconChevronUp,
  IconInbox,
  IconPlus,
  IconSend,
  IconUpload,
  IconX,
} from '@shared/ui/icons';
import { formatMoney } from '../financierFormat';
import { useAgentPermission } from '@entities/user';
import {
  Container,
  HeaderRow,
  TitleBox,
  Title,
  Subtitle,
  ActionsGroup,
  AlertBanner,
  AnalyticsHeroWrapper,
} from './styled';
import type { PipelineMetrics } from './types';
import { RequestsPipeline } from './RequestsPipeline';
import { CashThermometer } from './CashThermometer';
import { CompactSummary } from './CompactSummary';
import { RequestsFilterToolbar } from './RequestsFilterToolbar';
import { RequestsTable } from './RequestsTable';
import { RequestsModals } from './RequestsModals';
import { DecisionReasonDialog, type ReasonTarget } from './DecisionReasonDialog';

export function FinancierRequestsView() {
  const { can_write: canWrite } = useAgentPermission('finance');
  const {
    data: dashboardData,
    isLoading: isDashboardLoading,
    isError: isDashboardError,
    refetch: refetchDashboard,
  } = useGetFinancierDashboardQuery();
  const { data: registries = [] } = useListPaymentRegistriesQuery();

  const [selectedRegistryId, setSelectedRegistryId] = useState<string>('all');
  const [pipelineStage, setPipelineStage] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [moduleFilter, setModuleFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [inlineAmounts, setInlineAmounts] = useState<Record<string, number>>({});
  const [isAnalyticsCollapsed, setIsAnalyticsCollapsed] = useState(false);

  // Сброс выбора заявок при переключении фильтров и реестров (F22)
  useEffect(() => {
    setSelectedIds([]);
  }, [selectedRegistryId, pipelineStage, statusFilter, moduleFilter, search]);

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createRegistryOpen, setCreateRegistryOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [reconcileModalOpen, setReconcileModalOpen] = useState(false);
  const [markPaidItem, setMarkPaidItem] = useState<FinPaymentRequestRow | null>(null);
  const [exportResult, setExportResult] = useState<Export1cResult | null>(null);
  const [alertMsg, setAlertMsg] = useState<{ tone: 'success' | 'info' | 'warning'; text: string } | null>(null);
  // Отказ и отсрочка — с причиной: по заявке сотрудника он видит её в своей карточке.
  const [reasonFor, setReasonFor] = useState<ReasonTarget | null>(null);

  // Mutations
  const { data: requests = [], isLoading, isError, refetch } = useListPaymentRequestsQuery({
    registry_id: selectedRegistryId !== 'all' ? selectedRegistryId : undefined,
  });
  const [collectRequests, { isLoading: isCollecting }] = useCollectPaymentRequestsMutation();
  const [submitToCeo, { isLoading: isSubmitting }] = useSubmitRequestsToCeoMutation();
  const [export1C, { isLoading: isExporting }] = useExportApprovedTo1CMutation();
  const [decideRequest] = useDecidePaymentRequestMutation();
  const [sync1C, { isLoading: isSyncing1C }] = useSync1CApiMutation();

  // Доступная операционная ликвидность с дашборда (F16)
  const isLiquidityConfirmed = !isDashboardLoading && !isDashboardError && dashboardData != null;
  const availableCash = isLiquidityConfirmed
    ? dashboardData?.accounts_summary?.free_operational_cash ?? 0
    : null;
  const freeCash = availableCash ?? 0;

  // Инициализация inlineAmounts
  useEffect(() => {
    const map: Record<string, number> = {};
    for (const r of requests) {
      map[r.id] = r.approved_amount > 0 ? r.approved_amount : r.amount;
    }
    setInlineAmounts(map);
  }, [requests]);

  // Конвейер метрики
  const pipelineMetrics: PipelineMetrics = useMemo(() => {
    let formingCount = 0;
    let formingAmount = 0;
    let gatesPassedCount = 0;
    let gatesPassedAmount = 0;
    let approvedCount = 0;
    let approvedAmount = 0;
    let cutCarryoverTotal = 0;
    let inBankCount = 0;
    let inBankAmount = 0;
    let paidTotal = 0;
    let partiallyPaidTotal = 0;
    let unpaidTotal = 0;

    for (const r of requests) {
      // Для родительской заявки с перенесённым остатком берём её действующую сумму (approved_amount),
      // сохраняя баланс: родитель (60) + ребёнок -ОСТ (40) = исходное обязательство (100) (N06)
      const isParentWithCarryover = Boolean(
        r.carryover_request_id || (r.cut_amount > 0 && r.approved_amount > 0)
      );
      const activeLiability = isParentWithCarryover ? r.approved_amount : (r.requested_amount || r.amount);
      const amt = activeLiability;
      const appr = r.approved_amount || r.amount;

      // 1. Сформировано в реестре
      if (r.status === 'draft' || r.status === 'in_review') {
        formingCount += 1;
        formingAmount += amt;
      }

      // 2. Прошли 4 гейта
      const isGatesPassed =
        r.gate_contract_status === 'passed' &&
        r.gate_primary_doc_status === 'passed' &&
        (r.gate_budget_status === 'within_limit' || r.gate_budget_status === 'within_tolerance') &&
        (r.gate_account_routing === 'settlement_ok' || r.gate_account_routing === 'obs_targeted' || r.gate_account_routing === 'tax_reserve_ok');

      if (isGatesPassed) {
        gatesPassedCount += 1;
        gatesPassedAmount += amt;
      }

      // 3. Утверждено Гендиректором
      const isApprovedStep = ['approved', 'exported_1c', 'executed'].includes(r.status);
      if (isApprovedStep) {
        approvedCount += 1;
        approvedAmount += appr;
        cutCarryoverTotal += r.cut_amount || 0;
      }

      // 4. Выгружено в 1С / В банке
      const isInBankStep = ['exported_1c', 'executed'].includes(r.status);
      if (isInBankStep) {
        inBankCount += 1;
        inBankAmount += appr;
      }

      // 5. Факт по выписке (F18)
      const paid = r.paid_amount || 0;
      if (paid > 0) {
        paidTotal += paid;
      }
      if (r.payment_execution_status === 'partially_paid' || (paid > 0 && paid < appr)) {
        partiallyPaidTotal += paid;
      }
      if (isApprovedStep) {
        unpaidTotal += Math.max(0, appr - paid);
      }
    }

    return {
      formingCount,
      formingAmount,
      gatesPassedCount,
      gatesPassedAmount,
      approvedCount,
      approvedAmount,
      cutCarryoverTotal,
      inBankCount,
      inBankAmount,
      paidTotal,
      partiallyPaidTotal,
      unpaidTotal,
    };
  }, [requests]);

  // Расчёт Балансировщика ликвидности (F16, F18)
  const totalPaidInView = pipelineMetrics.paidTotal;
  // Непогашенный остаток по утверждённым заявкам строго равен сумме остатков по утверждённым строкам
  const remainingToDebit = pipelineMetrics.unpaidTotal;
  const projectedCashAfterRegistry = isLiquidityConfirmed ? freeCash - remainingToDebit : null;
  const isDeficit = isLiquidityConfirmed && projectedCashAfterRegistry !== null && projectedCashAfterRegistry < 0;
  const coveragePct = isLiquidityConfirmed && remainingToDebit > 0
    ? (freeCash / remainingToDebit) * 100
    : isLiquidityConfirmed
      ? 100
      : null;

  // Фильтрация заявок
  const filtered = useMemo(() => {
    return requests.filter((r) => {
      // 1. Фильтр реестра
      if (selectedRegistryId !== 'all' && r.registry_id !== selectedRegistryId) {
        return false;
      }

      // 2. Фильтр по клику на конвейер
      if (pipelineStage === 'forming') {
        if (r.status !== 'draft' && r.status !== 'in_review') return false;
      } else if (pipelineStage === 'gates_passed') {
        const passed =
          r.gate_contract_status === 'passed' &&
          r.gate_primary_doc_status === 'passed' &&
          (r.gate_budget_status === 'within_limit' || r.gate_budget_status === 'within_tolerance') &&
          (r.gate_account_routing === 'settlement_ok' || r.gate_account_routing === 'obs_targeted' || r.gate_account_routing === 'tax_reserve_ok');
        if (!passed) return false;
      } else if (pipelineStage === 'approved') {
        if (!['approved', 'exported_1c', 'executed'].includes(r.status)) return false;
      } else if (pipelineStage === 'in_bank') {
        if (!['exported_1c', 'executed'].includes(r.status)) return false;
      } else if (pipelineStage === 'executed') {
        if (
          (r.paid_amount || 0) <= 0 &&
          r.payment_execution_status !== 'paid' &&
          r.payment_execution_status !== 'partially_paid' &&
          r.status !== 'executed'
        ) {
          return false;
        }
      }

      // 3. Фильтры панели
      if (statusFilter !== 'all' && r.status !== statusFilter) return false;
      if (moduleFilter !== 'all' && r.source_module !== moduleFilter) return false;

      // 4. Поиск
      if (search.trim()) {
        const q = search.toLowerCase();
        const matchesNum = r.request_number.toLowerCase().includes(q);
        const matchesCp = r.counterparty_name.toLowerCase().includes(q);
        const matchesInn = r.counterparty_inn?.includes(q) ?? false;
        const matchesPurp = r.purpose.toLowerCase().includes(q);
        const matchesRef = r.source_doc_ref.toLowerCase().includes(q);
        const matchesDoc = r.bank_doc_numbers?.toLowerCase().includes(q) ?? false;
        if (!matchesNum && !matchesCp && !matchesInn && !matchesPurp && !matchesRef && !matchesDoc) {
          return false;
        }
      }

      return true;
    });
  }, [requests, selectedRegistryId, pipelineStage, statusFilter, moduleFilter, search]);

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selectedIds.includes(r.id));

  const toggleSelectAll = () => {
    if (allFilteredSelected) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filtered.map((r) => r.id));
    }
  };

  const toggleSelectRow = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Inline approval shortcuts (F25)
  const setPercentAmount = (reqId: string, baseAmount: number, pct: number) => {
    const rawVal = Math.round(baseAmount * pct * 100) / 100;
    const val = Math.max(0, Math.min(baseAmount, rawVal));
    setInlineAmounts((prev) => ({ ...prev, [reqId]: val }));
  };

  const handleInlineAmountChange = (reqId: string, val: number) => {
    setInlineAmounts((prev) => ({ ...prev, [reqId]: val }));
  };

  /** Решение по заявке; вернёт текст ошибки или `null`, если прошло. */
  const handleInlineDecide = async (
    req: FinPaymentRequestRow,
    decision: 'approved' | 'deferred' | 'rejected',
    reason?: string,
  ): Promise<string | null> => {
    const requested = req.requested_amount || req.amount;
    const rawApproved = inlineAmounts[req.id] ?? requested;
    const currentApproved = Math.max(
      0,
      Math.min(requested, Number.isFinite(rawApproved) ? rawApproved : 0)
    );

    try {
      const res = await decideRequest({
        id: req.id,
        decision,
        approved_amount: decision === 'approved' ? currentApproved : undefined,
        carryover_remainder: true,
        comment:
          currentApproved < requested && decision === 'approved'
            ? `Частичный акцепт: ${formatMoney(currentApproved)} из ${formatMoney(requested)}, остаток перенесён в след. реестр`
            : reason?.trim() || undefined,
      }).unwrap();

      const returnedStatus = res?.decided_request?.status;
      const successText =
        decision === 'approved'
          ? returnedStatus === 'ready_for_ceo'
            ? `Заявка ${req.request_number} проверена финансистом и подготовлена к согласованию Гендиректором`
            : `Заявка ${req.request_number} утверждена на ${formatMoney(currentApproved)}`
          : decision === 'deferred'
          ? `Заявка ${req.request_number} отложена`
          : `Заявка ${req.request_number} отклонена`;

      setAlertMsg({
        tone: 'success',
        text: successText,
      });
      refetch();
      return null;
    } catch (err: unknown) {
      const text = apiErrorText(err, 'Не удалось выполнить действие по заявке');
      setAlertMsg({
        tone: 'warning',
        text,
      });
      return text;
    }
  };

  // «Отложить» и «Отклонить» сначала спрашивают причину.
  const requestDecision = (
    req: FinPaymentRequestRow,
    decision: 'approved' | 'deferred' | 'rejected',
  ) => {
    if (decision === 'approved') {
      void handleInlineDecide(req, decision);
    } else {
      setReasonFor({ req, decision });
    }
  };

  const handleCollect = async () => {
    try {
      const res = await collectRequests().unwrap();
      setAlertMsg({
        tone: 'success',
        text: `Собрано ${res.collected_count} новых заявок из цехов и Хронума`,
      });
      refetch();
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось собрать заявки из цехов') });
    }
  };

  const handleSubmitCeo = async () => {
    if (selectedIds.length === 0) return;
    try {
      const res = await submitToCeo({ request_ids: selectedIds }).unwrap();
      setAlertMsg({
        tone: 'success',
        text: `Передано на акцепт Гендиректору: ${res.submitted_count} заявок`,
      });
      setSelectedIds([]);
      refetch();
    } catch (err: unknown) {
      setAlertMsg({
        tone: 'warning',
        text: apiErrorText(err, 'Не удалось передать заявки Гендиректору'),
      });
    }
  };

  const handleExport1C = async () => {
    try {
      const res = await export1C({
        request_ids: selectedIds.length > 0 ? selectedIds : undefined,
        registry_id: selectedRegistryId !== 'all' ? selectedRegistryId : undefined,
      } as Export1cInput & { registry_id?: string }).unwrap();
      const skipped = res.skipped_tax_requests ?? [];
      if (res.requests_count > 0) {
        setExportResult(res);
      } else {
        setAlertMsg({
          tone: 'info',
          text:
            skipped.length > 0
              ? `В файл для банка нечего выгружать. Налоги ЕНС проводятся в банк-клиенте:\n${skipped.join('\n')}`
              : 'В файл для банка нечего выгружать: нет акцептованных заявок с остатком к оплате.',
        });
      }
      refetch();
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось выгрузить платёжки в 1С') });
    }
  };

  const handleSync1C = async () => {
    try {
      const res = await sync1C({
        registry_id: selectedRegistryId !== 'all' ? selectedRegistryId : undefined,
      }).unwrap();
      setAlertMsg({
        tone: 'success',
        text: `1С API: ${res.message} (режим: ${res.connection_mode})`,
      });
      refetch();
    } catch (err: unknown) {
      setAlertMsg({ tone: 'warning', text: apiErrorText(err, 'Не удалось синхронизироваться с 1С') });
    }
  };

  const handleDownloadExcel = async () => {
    let token =
      localStorage.getItem('osnova_access_token') ||
      localStorage.getItem('osnova.auth.accessToken');
    if (!token) {
      try {
        const raw = localStorage.getItem('osnova.auth');
        if (raw) {
          token = (JSON.parse(raw) as { accessToken?: string })?.accessToken ?? null;
        }
      } catch {
        // ignore json parse error
      }
    }

    const url = `/api/v1/agents/finance/registries/export-excel${
      selectedRegistryId !== 'all' ? `?registry_id=${selectedRegistryId}` : ''
    }`;

    try {
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) {
        let errMsg = `Ошибка сервера (${res.status})`;
        try {
          const errData = await res.json();
          if (errData?.error) errMsg = String(errData.error);
        } catch {
          // not json
        }
        throw new Error(errMsg);
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `payment_registry_${selectedRegistryId}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
    } catch (err: unknown) {
      setAlertMsg({
        tone: 'warning',
        text: `Не удалось скачать реестр в Excel: ${err instanceof Error ? err.message : String(err)}`,
      });
    }
  };

  return (
    <Container>
      <HeaderRow>
        <TitleBox>
          <Title>Фабрика платежей и Реестр казначейства</Title>
          <Subtitle>
            Формирование реестра дня, 4 гейта финконтроля, инлайн-акцепт с автопереносом остатка (-ОСТ), выгрузка в 1С и сверка с выпиской.
          </Subtitle>
        </TitleBox>

        <ActionsGroup>
          <Button
            $variant="secondary"
            onClick={handleCollect}
            disabled={isCollecting || !canWrite}
          >
            <IconInbox size={16} />
            {isCollecting ? 'Сбор…' : 'Собрать из цехов'}
          </Button>

          <Button
            $variant="primary"
            onClick={handleSubmitCeo}
            disabled={selectedIds.length === 0 || isSubmitting || !canWrite}
          >
            <IconSend size={16} />
            {isSubmitting ? 'Отправка…' : `Передать Гендиректору (${selectedIds.length})`}
          </Button>

          <Button
            $variant="secondary"
            onClick={handleExport1C}
            disabled={isExporting || !canWrite}
          >
            <IconUpload size={16} />
            {isExporting ? 'Выгрузка…' : 'Выгрузить в 1С'}
          </Button>

          <Button
            $variant="secondary"
            onClick={() => setCreateModalOpen(true)}
            disabled={!canWrite}
          >
            <IconPlus size={16} />
            Новая заявка
          </Button>

          <Button
            $variant="ghost"
            onClick={() => setIsAnalyticsCollapsed((prev) => !prev)}
            aria-expanded={!isAnalyticsCollapsed}
            aria-controls="financier-analytics-hero"
            title={isAnalyticsCollapsed ? 'Развернуть блок аналитики' : 'Свернуть блок аналитики'}
          >
            {isAnalyticsCollapsed ? <IconBarChart size={16} /> : <IconChevronUp size={16} />}
            {isAnalyticsCollapsed ? 'Развернуть аналитику' : 'Свернуть аналитику'}
          </Button>
        </ActionsGroup>
      </HeaderRow>

      {alertMsg && (
        <AlertBanner $tone={alertMsg.tone}>
          <span>{alertMsg.text}</span>
          <Button $variant="ghost" $size="sm" onClick={() => setAlertMsg(null)} aria-label="Закрыть">
            <IconX size={14} />
          </Button>
        </AlertBanner>
      )}

      {/* 1 & 2. Аналитический блок (Конвейер + Балансировщик) / Компактный вид */}
      {isAnalyticsCollapsed ? (
        <CompactSummary
          pipelineStage={pipelineStage}
          metrics={pipelineMetrics}
          isDashboardLoading={isDashboardLoading}
          isDashboardError={isDashboardError}
          availableCash={availableCash}
          coveragePct={coveragePct}
          onExpand={() => setIsAnalyticsCollapsed(false)}
        />
      ) : (
        <AnalyticsHeroWrapper id="financier-analytics-hero">
          <RequestsPipeline
            pipelineStage={pipelineStage}
            onStageChange={setPipelineStage}
            metrics={pipelineMetrics}
          />

          <CashThermometer
            isLiquidityConfirmed={isLiquidityConfirmed}
            coveragePct={coveragePct}
            isDashboardLoading={isDashboardLoading}
            isDashboardError={isDashboardError}
            availableCash={availableCash}
            freeCash={freeCash}
            remainingToDebit={remainingToDebit}
            totalPaidInView={totalPaidInView}
            isDeficit={isDeficit}
            projectedCashAfterRegistry={projectedCashAfterRegistry}
            onRefetchDashboard={() => refetchDashboard()}
          />
        </AnalyticsHeroWrapper>
      )}

      {/* 3. Селектор Реестров платежей и панель действий */}
      <RequestsFilterToolbar
        selectedRegistryId={selectedRegistryId}
        onSelectRegistry={setSelectedRegistryId}
        registries={registries}
        onSync1C={handleSync1C}
        isSyncing1C={isSyncing1C}
        onOpenImport={() => setImportModalOpen(true)}
        onOpenReconcile={() => setReconcileModalOpen(true)}
        onDownloadExcel={handleDownloadExcel}
        onOpenCreateRegistry={() => setCreateRegistryOpen(true)}
        search={search}
        onSearchChange={setSearch}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        moduleFilter={moduleFilter}
        onModuleFilterChange={setModuleFilter}
        canWrite={canWrite}
      />

      {/* 4. Интерактивная таблица заявок */}
      <RequestsTable
        filtered={filtered}
        isLoading={isLoading}
        isError={isError}
        refetch={refetch}
        selectedIds={selectedIds}
        allFilteredSelected={allFilteredSelected}
        onToggleSelectAll={toggleSelectAll}
        onToggleSelectRow={toggleSelectRow}
        inlineAmounts={inlineAmounts}
        onInlineAmountChange={handleInlineAmountChange}
        onSetPercentAmount={setPercentAmount}
        onInlineDecide={requestDecision}
        onSetMarkPaidItem={setMarkPaidItem}
        onGateAlert={setAlertMsg}
        canWrite={canWrite}
      />

      {/* Модальные окна */}
      <RequestsModals
        exportResult={exportResult}
        onCloseExport={() => setExportResult(null)}
        createModalOpen={createModalOpen}
        onCloseCreateModal={() => setCreateModalOpen(false)}
        createRegistryOpen={createRegistryOpen}
        onCloseCreateRegistry={() => setCreateRegistryOpen(false)}
        onRegistryCreated={(newId) => {
          setSelectedRegistryId(newId);
          refetch();
        }}
        importModalOpen={importModalOpen}
        onCloseImportModal={() => setImportModalOpen(false)}
        reconcileModalOpen={reconcileModalOpen}
        onCloseReconcileModal={() => setReconcileModalOpen(false)}
        markPaidItem={markPaidItem}
        onCloseMarkPaidItem={() => setMarkPaidItem(null)}
        selectedRegistryId={selectedRegistryId}
        onSuccess={() => refetch()}
      />

      {reasonFor && (
        <DecisionReasonDialog
          key={`${reasonFor.req.id}:${reasonFor.decision}`}
          target={reasonFor}
          onClose={() => setReasonFor(null)}
          onConfirm={async (reason) => {
            const failed = await handleInlineDecide(reasonFor.req, reasonFor.decision, reason);
            if (!failed) setReasonFor(null);
            return failed;
          }}
        />
      )}
    </Container>
  );
}
